import classNames from 'classnames';
import DestinationCard from 'components/DestinationCard';
import Header from 'components/FiltersModal/Header';
import { Constrain, SectionTitle } from 'components/FiltersModal/components';
import { FILTERS_MODAL } from 'components/Scroller/constants';
import { SELECTED_FILTER } from 'components/SelectedFilters/constants';
import SwitchComponent from 'components/SwitchComponent';
import { AnalyticsContext } from 'contexts/AnalyticsContext';
import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import { BRAND_CEL, CONTAINERS, CUSTOM, DESTINATION, LABEL_MISSING_PORT_CODE, PORT } from 'contexts/CruiseSearchContext/constants';
import { gsap } from 'gsap';
import { useContext, useEffect, useRef, useState } from 'react';
import CustomFilters from '../CustomFilters';
import { ButtonWrap } from '../DeparturePort/components';
import FilterSearchBar from '../FilterSearchBar';
import DestinationFilters from './DestinationFilters';
import { Button, DestinationGrid, Section, Wrapper } from './components';

import { forEach } from 'lib/helpers';
import _groupBy from 'lodash/groupBy';
import _omitBy from 'lodash/omitBy';

import { useUser } from '@rcg/ui-user';
import { getBrandUISettings } from 'lib/utilities/Brands';
import { useFeatureFlags } from '../../../../hooks/LaunchDarkly/useFeatureFlags';

export const OPTIONS_LABEL = {
	ALL_DESTINATIONS: 'All destinations',
};
export const getDestinationOptions = (labelDestinations, labelPorts) => {
	return {
		destination: {
			key: DESTINATION,
			label: labelDestinations,
			filterName: DESTINATION,
			options: [],
		},
		port: {
			key: PORT,
			label: labelPorts,
			filterName: PORT,
			options: [],
		},
	};
};
/**
 * Destinations
 *
 * The Destinations filter component
 * listens to/sets the destination param of the search query
 */
const Destinations = () => {
	const {
		filterOptionsDictionary,
		removeFromSearchSelectingFilter,
		addToSearchSelectingFilter,
		searchSelecting,
		resetSearchSelectingFilter,
		facetOptions,
		fetchAndUpdateFacetOptions,
		globalization,
		getUniqueDestinationsState,
		brand,
		removeAdditionalFilters,
		promotionSelected,
	} = useContext(CruiseSearchContext);
	const { showImagesInDestinationFilter, showAllPortsFilter } = useFeatureFlags();
	const { utagPortClicked } = useContext(AnalyticsContext);
	const { user } = useUser();
	const { uiSettings } = getBrandUISettings(brand);
	const { itineraryCardsDictionary, filtersDictionary } = globalization?.dictionaries || {};

	const { labelPorts } = itineraryCardsDictionary;
	const { labelUniqueDestination, labelAllDestinations, labelAllPorts, labelDestinationsHeader, inputPortsPlaceholder } = filtersDictionary;

	const DESTINATION_OPTIONS = getDestinationOptions(labelDestinationsHeader, labelPorts);

	const destinationTitleOptions = getDestinationOptions(labelAllDestinations, labelAllPorts);

	const { custom = [] } = filterOptionsDictionary || {};

	const [portOptionsSelected, setPortOptionsSelected] = useState();

	let nextSelectedPort = searchSelecting?.port
		?.map((port) => {
			const filteredOption = filterOptionsDictionary?.port?.options.filter((e) => e.code === port);
			const [{ label, code }] = filteredOption;
			return {
				portLabel: label,
				portCode: code,
			};
		})
		.sort((a, b) => (a.portLabel > b.portLabel ? 1 : -1));

	const [allowingOption, setAllowingOption] = useState(DESTINATION_OPTIONS.destination.key);
	const [allowingTitleOption, setAllowingTitleOption] = useState(destinationTitleOptions.destination.key);

	const [optionsLoaded, setOptionsLoaded] = useState(filterOptionsDictionary?.destination);

	if (optionsLoaded) {
		const destinations = filterOptionsDictionary?.destination?.options;
		DESTINATION_OPTIONS.destination.options = destinations || [];
		DESTINATION_OPTIONS.port.options = filterOptionsDictionary?.port?.options || [];
	}
	useEffect(() => {
		if (searchSelecting?.port) {
			setPortOptionsSelected(nextSelectedPort);
		}
		if (filterOptionsDictionary?.destination) {
			setOptionsLoaded(true);
		}
	}, [filterOptionsDictionary?.destination, optionsLoaded, searchSelecting]);

	const handleTapSwitch = (filterName) => {
		if (filterName === PORT) {
			if (brand === BRAND_CEL.code) {
				const link_name = window?.location?.href;
				const extra = {
					VDSID: user.vdsId,
					link_name,
				};
				utagPortClicked({ extra });
			} else {
				utagPortClicked({});
			}
		}
		setAllowingOption(filterName);
		setAllowingTitleOption(filterName);
	};

	const addBahamasToSelectedDestinations = () => {
		addToSearchSelectingFilter('destination', 'BAHAM', fetchAndUpdateFacetOptions);
	};

	const updateSelection = (code, filterName = allowingOption) => {
		if (searchSelecting?.[filterName]?.includes(code)) {
			removeFromSearchSelectingFilter(filterName, code, fetchAndUpdateFacetOptions);
		} else {
			const option = _omitBy(DESTINATION_OPTIONS, (item) => item.filterName === filterName);
			const filterNameOption = Object.keys(option || {})?.[0];

			resetSearchSelectingFilter(filterNameOption, addToSearchSelectingFilter(filterName, code, fetchAndUpdateFacetOptions));
			if (code === 'CARIB' && facetOptions?.destination?.BAHAM.count > 0) {
				addBahamasToSelectedDestinations();
			}
		}
	};
	const sectionTitleRef = useRef();
	const destinationGridRef = useRef();

	const resetFilterOptions = () => {
		const uniqueDestinations = getUniqueDestinationsState(CONTAINERS.FILTER_DESTINATION, promotionSelected);
		forEach(uniqueDestinations, (unique) => {
			if (unique.selected) {
				removeAdditionalFilters();
				removeFromSearchSelectingFilter(CUSTOM, unique.code, fetchAndUpdateFacetOptions, unique.code);
			}
		});
		resetSearchSelectingFilter(PORT);
		resetSearchSelectingFilter(DESTINATION, fetchAndUpdateFacetOptions);
	};
	const id = useRef(Date.now());

	useEffect(() => {
		gsap.fromTo(
			sectionTitleRef.current,
			{
				opacity: 0,
			},
			{
				opacity: 1,
				ease: 'cubic-bezier(0,0,0,0)',
			},
		);
		gsap.fromTo(
			destinationGridRef.current,
			{
				opacity: 0,
			},
			{
				opacity: 1,
				ease: 'cubic-bezier(0,0,0,0)',
			},
		);
	}, []);

	const getSortOptions = (options) => {
		const filterEmpty = Array.isArray(options) ? options.filter((element) => element.code) : [];
		const listOrdered = filterEmpty.sort((a, b) => (a.label > b.label ? 1 : -1));
		const group = _groupBy(listOrdered, 'label[0]');
		const listGroups = Object.keys(group).map((key) => {
			const items = group[key];

			return { code: key, items };
		});

		return listGroups;
	};

	const getListOptions = (data) => {
		const { isDestination, option, i, destKey, label, key } = data;
		const selected = searchSelecting?.[destKey]?.includes(option?.code) || false;
		const disabled = isDestination ? !facetOptions[DESTINATION]?.[option?.code]?.count > 0 : !facetOptions[PORT]?.[option?.code]?.count > 0;
		return (
			<SwitchComponent toogle={isDestination ? showImagesInDestinationFilter : false} key={`${id.current}${option?.code}-${key}-${i}`}>
				<DestinationCard
					index={i}
					option={option}
					type={destKey}
					scrollerId={FILTERS_MODAL}
					allowingOption={allowingOption}
					key={`${id.current}${option.code}-${key}-${i}`}
					updateSelection={() => updateSelection(option.code)}
					selected={selected}
					disabled={selected ? false : disabled}
				/>
				<ButtonWrap
					key={`${option.label}-${i}`}
					className={classNames({
						disabled: selected ? false : disabled,
					})}
				>
					<Button id={`${key}-label-${option.code}`} showX={false} onClick={() => updateSelection(option.code)} selected={selected}>
						{label}
					</Button>
				</ButtonWrap>
			</SwitchComponent>
		);
	};

	const getSectionByGroup = (group, destKey, isDestination) => {
		return (
			<Section>
				<SectionTitle
					ref={sectionTitleRef}
					title={group?.code}
					className={classNames({
						portSection: true,
					})}
				/>
				<DestinationGrid showImage={showImagesInDestinationFilter} ref={destinationGridRef} gap={uiSettings.filtersModal.filtersGrid.gap}>
					{group?.items?.map((option, i) => {
						const label = (option?.label !== LABEL_MISSING_PORT_CODE && option?.label) || option?.code;
						const key = 'port-card';
						return getListOptions({ isDestination, option, i, destKey, label, key });
					})}
				</DestinationGrid>
			</Section>
		);
	};

	return (
		<>
			<Header title={labelDestinationsHeader} onReset={() => resetFilterOptions()} />
			<Constrain>
				{showAllPortsFilter && <DestinationFilters allowingOption={allowingOption} setAllowingOption={handleTapSwitch} />}
				<FilterSearchBar searchesAllowing={[PORT]} scrollerId={FILTERS_MODAL} placeholder={inputPortsPlaceholder} container={allowingOption} />
				{allowingOption === PORT && (
					<Wrapper>
						{portOptionsSelected?.map((option) => {
							return (
								<Button className={SELECTED_FILTER} key={option.portCode} selected onClick={() => updateSelection(option.portCode)} showX>
									{(option?.portLabel !== LABEL_MISSING_PORT_CODE && option?.portLabel) || option?.portCode}
								</Button>
							);
						})}
					</Wrapper>
				)}
				{custom?.options?.length > 0 && allowingOption === DESTINATION && (
					<>
						<CustomFilters title={labelUniqueDestination} keyName={'filter_destination'} />
					</>
				)}
				<SectionTitle ref={sectionTitleRef} title={destinationTitleOptions[allowingTitleOption].label} />
				{Object.keys(DESTINATION_OPTIONS).map((key) => {
					let { options, key: destKey } = DESTINATION_OPTIONS[key];
					const isDestination = DESTINATION_OPTIONS[key].key === DESTINATION;

					if (!isDestination) {
						options = getSortOptions(options);
						return options.map((option) => {
							return destKey === allowingOption && getSectionByGroup(option, destKey, isDestination);
						});
					} else {
						const key = 'destination-card';
						return (
							<DestinationGrid
								showImage={showImagesInDestinationFilter}
								ref={destinationGridRef}
								gap={uiSettings.filtersModal.filtersGrid.gap}
								key={destKey}
							>
								{options.map((option, i) => {
									return destKey === allowingOption && getListOptions({ isDestination, option, i, destKey, label: option.label || option.code, key });
								})}
							</DestinationGrid>
						);
					}
				})}
			</Constrain>
		</>
	);
};
export default Destinations;
